import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import { CompareArrows, Store, Check, Close, Edit } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import comparisonService from '../shared/services/comparisonService';
import { useShops } from '../shared/contexts/ShopsContext';
import apiClient from '../shared/services/api';

const Comparison = () => {
  const [selectedShops, setSelectedShops] = useState([]);
  const [priceChangeDialog, setPriceChangeDialog] = useState({
    open: false,
    shopId: null,
    offerId: null,
    currentPrice: null,
    newPrice: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [priceChangeLoading, setPriceChangeLoading] = useState(false);
  const { shops, loading: shopsLoading, error: shopsError } = useShops();
  const queryClient = useQueryClient();

  const shop1Id = selectedShops[0];
  const shop2Id = selectedShops[1];

  // Fetch offers for both shops
  const {
    data: shop1OffersResp = {},
    isLoading: shop1Loading,
    error: shop1Error,
  } = useQuery({
    queryKey: ['shopOffers', shop1Id],
    queryFn: () => shop1Id ? comparisonService.getShopOffers(shop1Id) : Promise.resolve({ offers: [] }),
    enabled: !!shop1Id,
  });

  const {
    data: shop2OffersResp = {},
    isLoading: shop2Loading,
    error: shop2Error,
  } = useQuery({
    queryKey: ['shopOffers', shop2Id],
    queryFn: () => shop2Id ? comparisonService.getShopOffers(shop2Id) : Promise.resolve({ offers: [] }),
    enabled: !!shop2Id,
  });

  const shop1Offers = shop1OffersResp.offers || [];
  const shop2Offers = shop2OffersResp.offers || [];

  // Handle price change dialog
  const handleOpenPriceChange = (shopId, offerId, currentPrice) => {
    setPriceChangeDialog({
      open: true,
      shopId,
      offerId,
      currentPrice,
      newPrice: currentPrice ? currentPrice.toString() : '',
    });
  };

  const handleClosePriceChange = () => {
    setPriceChangeDialog({
      open: false,
      shopId: null,
      offerId: null,
      currentPrice: null,
      newPrice: '',
    });
  };

  const handlePriceChange = async () => {
    const { shopId, offerId, newPrice } = priceChangeDialog;
    
    if (!newPrice || parseFloat(newPrice) <= 0) {
      setSnackbar({
        open: true,
        message: 'Wprowadź poprawną cenę (większą od 0)',
        severity: 'error'
      });
      return;
    }

    setPriceChangeLoading(true);
    try {
      await apiClient.post(`/orders/${shopId}/price`, {
        amount: parseFloat(newPrice),
        offerId: offerId
      });

      setSnackbar({
        open: true,
        message: 'Cena została zmieniona pomyślnie',
        severity: 'success'
      });

      // Update the specific offer in the local state for immediate visual feedback
      if (shopId === shop1Id) {
        queryClient.setQueryData(['shopOffers', shopId], (oldData) => {
          if (!oldData?.offers) return oldData;
          return {
            ...oldData,
            offers: oldData.offers.map(offer => 
              offer.id === offerId 
                ? {
                    ...offer,
                    sellingMode: {
                      ...offer.sellingMode,
                      price: {
                        ...offer.sellingMode.price,
                        amount: newPrice.toString()
                      }
                    }
                  }
                : offer
            )
          };
        });
      } else if (shopId === shop2Id) {
        queryClient.setQueryData(['shopOffers', shopId], (oldData) => {
          if (!oldData?.offers) return oldData;
          return {
            ...oldData,
            offers: oldData.offers.map(offer => 
              offer.id === offerId 
                ? {
                    ...offer,
                    sellingMode: {
                      ...offer.sellingMode,
                      price: {
                        ...offer.sellingMode.price,
                        amount: newPrice.toString()
                      }
                    }
                  }
                : offer
            )
          };
        });
      }
      
      handleClosePriceChange();
    } catch (error) {
      console.error('Error changing price:', error);
      setSnackbar({
        open: true,
        message: `Błąd podczas zmiany ceny: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setPriceChangeLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Create display rows with matched offers
  const createDisplayRows = () => {
    const rows = [];
    const processedShop2Offers = new Set();
    const shop2OffersByExternalId = new Map();

    // Create map of shop2 offers by external ID
    shop2Offers.forEach(offer => {
      if (offer.external?.id) {
        shop2OffersByExternalId.set(offer.external.id, offer);
      }
    });

    // Process shop1 offers
    shop1Offers.forEach(shop1Offer => {
      const externalId = shop1Offer.external?.id;
      const shop2Offer = externalId ? shop2OffersByExternalId.get(externalId) : null;

      if (shop2Offer) {
        rows.push({
          shop1Offer,
          shop2Offer,
          hasMatch: true
        });
        processedShop2Offers.add(shop2Offer.id);
      } else {
        rows.push({
          shop1Offer,
          shop2Offer: null,
          hasMatch: false
        });
      }
    });

    // Add remaining shop2 offers
    shop2Offers.forEach(shop2Offer => {
      if (!processedShop2Offers.has(shop2Offer.id)) {
        rows.push({
          shop1Offer: null,
          shop2Offer,
          hasMatch: false
        });
      }
    });

    return rows;
  };

  const displayRows = createDisplayRows();

  const getShopName = (shopId) => {
    const shop = shops.find(s => s.id === shopId);
    return shop ? shop.name : `Sklep ${shopId}`;
  };

  const getPriceDifference = (shop1Offer, shop2Offer) => {
    if (!shop1Offer?.sellingMode?.price?.amount || !shop2Offer?.sellingMode?.price?.amount) {
      return null;
    }

    const price1 = parseFloat(shop1Offer.sellingMode.price.amount);
    const price2 = parseFloat(shop2Offer.sellingMode.price.amount);
    const difference = price1 - price2;
    const percentage = ((difference / price2) * 100).toFixed(1);
    
    return {
      difference: difference.toFixed(2),
      percentage: percentage,
      isLower: difference < 0
    };
  };

  const renderOfferCell = (offer, isShop1 = true, rowData = null) => {
    if (!offer) return <TableCell></TableCell>;

    // Debug: loguj strukturę oferty aby zobaczyć dostępne pola
    if (isShop1) {
      console.log('Struktura oferty:', offer);
    }

    const price = offer.sellingMode?.price?.amount;
    const currency = offer.sellingMode?.price?.currency || 'PLN';
    const shopId = isShop1 ? shop1Id : shop2Id;
    
    // Sprawdź czy to jest niższa cena
    const otherOffer = isShop1 ? rowData?.shop2Offer : rowData?.shop1Offer;
    const otherPrice = otherOffer?.sellingMode?.price?.amount;
    
    const isLowerPrice = price && otherPrice && parseFloat(price) < parseFloat(otherPrice);
    const isHigherPrice = price && otherPrice && parseFloat(price) > parseFloat(otherPrice);

    return (
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          {offer.primaryImage?.url && (
            <Avatar src={offer.primaryImage.url} variant="rounded" sx={{ width: 40, height: 40 }} />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500}>{offer.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              EAN: {offer.gtin || offer.ean || offer.barcode || 'Brak'} | External ID: {offer.external?.id || 'Brak'}
            </Typography>
            {price && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography 
                  variant="body2" 
                  fontWeight={600}
                  sx={{ 
                    color: isLowerPrice ? 'error.main' : isHigherPrice ? 'success.main' : 'text.primary',
                  }}
                >
                  {parseFloat(price).toFixed(2)} {currency}
                  {isLowerPrice && ' ⬇️'}
                  {isHigherPrice && ' ⬆️'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => handleOpenPriceChange(shopId, offer.id, price)}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  Zmień cenę
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>
    );
  };

  if (shopsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (shopsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Błąd podczas ładowania sklepów: {shopsError.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        <CompareArrows sx={{ mr: 1, verticalAlign: 'middle' }} />
        Porównanie między sklepami
      </Typography>

      {/* Shop Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Wybierz dwa sklepy do porównania
          </Typography>
          <Grid container spacing={2}>
            {shops.map((shop) => (
              <Grid item xs={12} sm={6} md={4} key={shop.id}>
                <Button
                  variant={selectedShops.includes(shop.id) ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedShops(prev => {
                      if (prev.includes(shop.id)) {
                        return prev.filter(id => id !== shop.id);
                      } else if (prev.length < 2) {
                        return [...prev, shop.id];
                      } else {
                        return [prev[1], shop.id];
                      }
                    });
                  }}
                  startIcon={<Store />}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    height: 48,
                  }}
                >
                  {shop.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State for Offers */}
      {(shop1Loading || shop2Loading) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography variant="body1">
                Ładowanie wszystkich ofert z wybranych sklepów...
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              To może potrwać kilka chwil w zależności od liczby ofert w sklepach
            </Typography>
          </CardContent>
        </Card>
      )}

      {selectedShops.length < 2 && (
        <Alert severity="info">
          Wybierz dokładnie dwa sklepy, aby zobaczyć porównanie.
        </Alert>
      )}

      {selectedShops.length === 2 && (shop1Loading || shop2Loading) && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {selectedShops.length === 2 && (shop1Error || shop2Error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Błąd podczas pobierania ofert:
          <br />
          {shop1Error && (<span>Sklep 1: {shop1Error.message}<br /></span>)}
          {shop2Error && (<span>Sklep 2: {shop2Error.message}</span>)}
        </Alert>
      )}

      {/* Results Summary */}
      {selectedShops.length === 2 && !shop1Loading && !shop2Loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {getShopName(shop1Id)}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {shop1Offers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    załadowanych ofert
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {getShopName(shop2Id)}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {shop2Offers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    załadowanych ofert
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Porównanie {displayRows.length} wierszy (dopasowania + unikalne oferty)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Comparison Table */}
      {selectedShops.length === 2 && !shop1Loading && !shop2Loading && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>
                  {getShopName(shop1Id)}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>
                  {getShopName(shop2Id)}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>
                  Różnica cenowa
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>
                  Powiązanie
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, index) => {
                const priceDiff = getPriceDifference(row.shop1Offer, row.shop2Offer);
                
                return (
                  <TableRow key={index}>
                    {renderOfferCell(row.shop1Offer, true, row)}
                    {renderOfferCell(row.shop2Offer, false, row)}
                    <TableCell align="center">
                      {priceDiff ? (
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{ 
                              color: priceDiff.isLower ? 'error.main' : 'success.main'
                            }}
                          >
                            {priceDiff.isLower ? '-' : '+'}{priceDiff.difference} PLN
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({priceDiff.isLower ? '-' : '+'}{priceDiff.percentage}%)
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Brak danych
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {row.hasMatch ? (
                        <Chip icon={<Check />} label="TAK" color="success" size="small" />
                      ) : (
                        <Chip icon={<Close />} label="NIE" color="error" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Price Change Dialog */}
      <Dialog 
        open={priceChangeDialog.open} 
        onClose={handleClosePriceChange} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={priceChangeLoading}
        onBackdropClick={priceChangeLoading ? undefined : handleClosePriceChange}
      >
        <DialogTitle>
          Zmień cenę oferty
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Aktualna cena: {priceChangeDialog.currentPrice ? `${parseFloat(priceChangeDialog.currentPrice).toFixed(2)} PLN` : 'Brak'}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Nowa cena (PLN)"
              type="number"
              fullWidth
              variant="outlined"
              value={priceChangeDialog.newPrice}
              onChange={(e) => setPriceChangeDialog(prev => ({ ...prev, newPrice: e.target.value }))}
              inputProps={{
                step: "0.01",
                min: "0.01"
              }}
              disabled={priceChangeLoading}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePriceChange} disabled={priceChangeLoading}>Anuluj</Button>
          <Button onClick={handlePriceChange} variant="contained" color="primary" disabled={priceChangeLoading}>
            {priceChangeLoading ? <CircularProgress size={24} /> : 'Zmień cenę'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Comparison; 